import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Auth } from '@angular/fire/auth'
import { doc, Firestore, getDoc, setDoc } from '@angular/fire/firestore'
import { getDownloadURL, ref, Storage, uploadBytes } from '@angular/fire/storage'
import { UntypedFormControl, Validators } from '@angular/forms'
import { MatButtonToggleChange } from '@angular/material/button-toggle'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ActivatedRoute, Router } from '@angular/router'
import moment from 'moment'
import * as PIXI from 'pixi.js'
import { filter, map, switchMap } from 'rxjs/operators'
import { DocumentStorageService } from 'src/app/service/document-storage.service'
import { DocumentEntry, DocumentType } from 'src/domain/document'
import { Dog, DogSex } from 'src/domain/dog'
import { mapFirebaseDog } from '../service/mapper/dogMapper'
import {
  DOG_PROFILE_IMG_X,
  DOG_PROFILE_IMG_Y,
  EXPIRY_FONT_STYLE,
  ID_CARD_EXPIRY_TEXT_X,
  ID_CARD_EXPIRY_TEXT_Y,
  ID_CARD_NUMBER_FONT_STYLE,
  ID_CARD_NUMBER_X,
  ID_CARD_NUMBER_Y,
  IDCARD_DOCUMENT_TYPE,
  IDCARD_META,
  USER_DATA_FONT_STYLE,
  USER_DATA_X_POS,
  USER_DATA_Y_DELTA,
  USER_DATA_Y_POS,
  USER_NAME_FONT_STYLE,
} from './idcard-render.constants'
import { IdCardRenderService } from './idcard-render.service'
import { IdCardRenewState } from './idcard-renew.service'

const EXPIRY_TIME_FORMAT = 'yyyy.MM.DD.'

@Component({
    templateUrl: 'idcard.component.html',
    styleUrls: ['idcard.component.scss'],
    standalone: false
})
export class IdCardComponent implements OnInit, OnDestroy {
  dog!: Dog
  dogId!: string

  trainerNameControl = new UntypedFormControl(null, Validators.required)
  idCardNumberControl = new UntypedFormControl(null, Validators.required)
  idCardExpiryControl = new UntypedFormControl(
    moment().add(1, 'year').format(EXPIRY_TIME_FORMAT),
    Validators.pattern(/^\d{4}\.\d{2}\.\d{2}\.?$/)
  )

  @ViewChild('container')
  container!: ElementRef

  app!: PIXI.Application
  dogDataAdded = false

  dogProfile!: PIXI.Sprite
  trainerNameText?: PIXI.Text
  idCardNumberText?: PIXI.Text
  idCardExpiryText?: PIXI.Text

  selectedIdCardType!: keyof typeof IDCARD_META
  profileImageBlob!: Blob
  profileImageUrl!: string
  isSaving = false

  private renewState?: IdCardRenewState
  // true when profileImageUrl still points at a renewed document's existing photo rather than a
  // fresh upload - cleared as soon as the user picks a new file, so saveAndUpload knows whether
  // it can skip re-uploading an unchanged photo
  private isPhotoCarriedOver = false

  constructor(
    private storage: Firestore,
    private auth: Auth,
    private fileStorage: Storage,
    private snack: MatSnackBar,
    private docStorage: DocumentStorageService,
    private renderService: IdCardRenderService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.renewState = this.router.currentNavigation()?.extras?.state?.['renewState'] as IdCardRenewState | undefined
  }

  ngOnDestroy(): void {
    // PIXI's shared texture pool can throw here (e.g. TexturePool.returnTexture on textures
    // loaded via PIXI.Assets) - left uncaught, this happens mid route-deactivation and can
    // interrupt Angular's navigation to the next route.
    try {
      this.app?.destroy()
    } catch (e) {
      console.error('Failed to destroy idcard PIXI app', e)
    }
  }

  get mappedDocumentType(): DocumentType | undefined {
    return this.selectedIdCardType ? IDCARD_DOCUMENT_TYPE[this.selectedIdCardType] : undefined
  }

  canSaveAndUpload(): boolean {
    return (
      !!this.mappedDocumentType &&
      !!this.dog &&
      !!this.profileImageBlob &&
      this.trainerNameControl.valid &&
      this.idCardNumberControl.valid &&
      this.idCardExpiryControl.valid
    )
  }

  idCardTypeChange(evt: MatButtonToggleChange) {
    this.selectIdCardType(evt.value)
  }

  selectIdCardType(value: keyof typeof IDCARD_META) {
    this.selectedIdCardType = value
    const idCard = IDCARD_META[value]
    if (!idCard) {
      alert('Valami félrement. Nincs adat ehhez a kártya típushoz')
      return
    }

    if (this.app) {
      try {
        this.app.destroy(true)
      } catch (e) {
        console.error('Failed to destroy previous idcard PIXI app', e)
      }
      this.trainerNameText = undefined
      this.idCardNumberText = undefined
      this.idCardExpiryText = undefined
    }

    setTimeout(async () => {
      this.dogDataAdded = false
      this.app = new PIXI.Application()
      await this.app.init({ width: idCard.size[0], height: idCard.size[1], backgroundColor: 0xffffff })
      var backgroundTexture = await PIXI.Assets.load(idCard.backgroundUrl)
      const bg = PIXI.Sprite.from(backgroundTexture)
      this.app.stage.addChild(bg)
      // the canvas keeps its full-resolution pixel buffer for downloads/uploads - this only
      // shrinks how large it's displayed on the page. Set directly (rather than via CSS) since
      // this canvas is appended outside Angular's template, so it's not covered by the
      // component's scoped stylesheet.
      this.app.canvas.style.maxWidth = '600px'
      this.app.canvas.style.width = '100%'
      this.app.canvas.style.height = 'auto'
      this.container.nativeElement.appendChild(this.app.canvas)
      this.addDogDataToScreen()
    })
  }

  ngOnInit(): void {
    this.trainerNameControl.valueChanges.subscribe({
      next: (name) => this.renderTrainerName(name),
    })
    this.idCardNumberControl.valueChanges.subscribe({
      next: (num) => this.renderIdCardNumber(num),
    })
    this.idCardExpiryControl.valueChanges.subscribe({
      next: (txt) => this.renderExpiry(txt),
    })

    this.route.paramMap
      .pipe(
        map((params) => params.get('dogId')),
        filter((x) => !!x),
        switchMap((dogId) => {
          this.dogId = dogId!
          return getDoc(doc(this.storage, 'dogs', this.dogId))
        })
      )
      .subscribe({
        next: async (dogDoc) => {
          if (dogDoc.exists()) {
            this.dog = mapFirebaseDog(dogDoc.data() as Dog)
            console.log(this.dog)

            if (this.renewState) {
              this.trainerNameControl.setValue(this.renewState.trainerName)
              this.idCardNumberControl.setValue(this.renewState.idNumber)
              this.idCardExpiryControl.setValue(this.renewState.expiryText)
              if (this.renewState.clientPhotoUrl) {
                await this.loadExistingPhoto(this.renewState.clientPhotoUrl)
              }
              this.selectIdCardType(this.renewState.idCardType)
            } else {
              this.trainerNameControl.setValue(this.dog.owner.name)
            }

            this.addDogDataToScreen()
          } else {
            console.log('Doc document not exists', dogDoc)
          }
        },
      })
  }

  private renderTrainerName(name: string) {
    if (name && this.app) {
      if (!this.trainerNameText) {
        this.trainerNameText = new PIXI.Text({
          text: name,
          style: USER_NAME_FONT_STYLE
        })
        // this.trainerNameText.anchor.set(0.5, 0)
        this.trainerNameText.x = USER_DATA_X_POS
        this.trainerNameText.y = USER_DATA_Y_POS
        this.app.stage.addChild(this.trainerNameText)
      } else {
        this.trainerNameText.text = name
      }
    }
  }

  private renderIdCardNumber(num: string) {
    if (num && this.app) {
      num = num.toUpperCase()
      if (!this.idCardNumberText) {
        this.idCardNumberText = new PIXI.Text({
          text: num,
          style: ID_CARD_NUMBER_FONT_STYLE,
        })
        this.idCardNumberText.anchor.set(1, 1)
        this.idCardNumberText.x = ID_CARD_NUMBER_X
        this.idCardNumberText.y = ID_CARD_NUMBER_Y
        this.app.stage.addChild(this.idCardNumberText)
      } else {
        this.idCardNumberText.text = num
      }
    }
  }

  private renderExpiry(expiryText: string) {
    if (this.app && expiryText) {
      if (!this.idCardExpiryText) {
        this.idCardExpiryText = new PIXI.Text({
          text: expiryText, 
          // width: 330,
          style: EXPIRY_FONT_STYLE
        })
        this.idCardExpiryText.x = ID_CARD_EXPIRY_TEXT_X
        this.idCardExpiryText.y = ID_CARD_EXPIRY_TEXT_Y
        this.app.stage.addChild(this.idCardExpiryText)
      } else {
        this.idCardExpiryText.text = expiryText
      }
    }
  }

  private renderDogProfile() {
    if (this.app && this.dogProfile) {
      setTimeout(() => {
        this.app.stage.addChild(this.dogProfile)
      })
    }
  }

  mapDogSex(sex: DogSex): string {
    switch (sex) {
      case DogSex.IVAROS_KAN:
        return 'Ivaros kan'
      case DogSex.IVAROS_SZUKE:
        return 'Ivaros szuka'
      case DogSex.IVARTALAN_KAN:
        return 'Ivartalan kan'
      case DogSex.IVARTALAN_SZUKA:
        return 'Ivartalan szuka'
      default:
        return sex
    }
  }

  addDogDataToScreen() {
    // base Y is the starting position and delta is between texts
    const baseY = USER_DATA_Y_POS + USER_DATA_Y_DELTA

    if (!this.app || !this.dog || this.dogDataAdded) {
      return
    }

    if (!this.trainerNameText) {
      this.renderTrainerName(this.trainerNameControl.value)
    }

    if (!this.idCardExpiryText) {
      this.renderExpiry(this.idCardExpiryControl.value)
    }

    if (!this.idCardNumberText) {
      this.renderIdCardNumber(this.idCardNumberControl.value)
    }

    this.renderDogProfile()

    const dogName = new PIXI.Text({
      text: this.dog.name,
      style: USER_NAME_FONT_STYLE
    })
    dogName.x = USER_DATA_X_POS
    dogName.y = baseY

    const breed = new PIXI.Text({
      text: this.capitalize(this.dog.breed),
      style: USER_DATA_FONT_STYLE
    })
    breed.x = USER_DATA_X_POS
    breed.y = baseY + 1 * USER_DATA_Y_DELTA

    const chipNumber = new PIXI.Text({
      text: this.dog.chipNumber,
      style: USER_DATA_FONT_STYLE
    })
    chipNumber.x = USER_DATA_X_POS
    chipNumber.y = baseY + 2 * USER_DATA_Y_DELTA

    this.app.stage.addChild(dogName)
    this.app.stage.addChild(breed)
    this.app.stage.addChild(chipNumber)

    this.dogDataAdded = true
  }

  private capitalize(str: string) {
    if (str) {
      const firstLetter = str.charAt(0).toUpperCase()
      return str.length > 1 ? firstLetter + str.slice(1) : firstLetter
    }
    return undefined
  }

  async dogImageSelected(event: { url: string; blob: Blob }) {
    // user picked a new photo - any carried-over renewal photo no longer applies
    this.isPhotoCarriedOver = false
    await this.setDogProfileImage(event.url, event.blob)
  }

  private async loadExistingPhoto(url: string) {
    const blob = await fetch(url).then((res) => res.blob())
    await this.setDogProfileImage(url, blob)
    this.isPhotoCarriedOver = true
  }

  private async setDogProfileImage(url: string, blob: Blob) {
    this.profileImageBlob = blob
    this.profileImageUrl = url

    const dogImageAsset = await PIXI.Assets.load({ src: url, loadParser: 'loadTextures' })
    if (this.dogProfile) {
      this.dogProfile.destroy()
    }

    this.dogProfile = PIXI.Sprite.from(dogImageAsset)
    this.dogProfile.anchor.set(0.5)
    this.dogProfile.x = DOG_PROFILE_IMG_X
    this.dogProfile.y = DOG_PROFILE_IMG_Y

    this.renderDogProfile()
  }

  private renderFinalCardBlob(): Promise<Blob> {
    return this.renderService.renderCardBlob({
      idCardType: this.selectedIdCardType,
      dog: this.dog,
      trainerName: this.trainerNameControl.value,
      idNumber: this.idCardNumberControl.value,
      expiryText: this.idCardExpiryControl.value,
      photoUrl: this.profileImageUrl,
    })
  }

  canDownload(): boolean {
    return !!this.app && this.dogDataAdded
  }

  async downloadCard() {
    if (!this.canDownload()) {
      return
    }

    const cardBlob = await this.renderFinalCardBlob()
    const url = URL.createObjectURL(cardBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${this.dog?.name || 'igazolvany'}_${this.selectedIdCardType}.png`
    link.click()
    URL.revokeObjectURL(url)
  }

  async saveAndUpload() {
    const docType = this.mappedDocumentType
    if (!this.canSaveAndUpload() || !docType) {
      return
    }

    const confirmed = confirm(
      `Biztosan feltölti ${this.dog.name} igazolványát?\n\n` +
        `Kutya: ${this.dog.name}\n` +
        `Sorszám: ${this.idCardNumberControl.value}\n` +
        `Érvényesség vége: ${this.idCardExpiryControl.value}`
    )
    if (!confirmed) {
      return
    }

    this.isSaving = true
    try {
      let clientPhotoUrl = this.isPhotoCarriedOver ? this.profileImageUrl : undefined
      if (!clientPhotoUrl) {
        const clientPhotoRef = ref(this.fileStorage, `dogs/${this.dogId}/idcard/client_${crypto.randomUUID()}.jpg`)
        await uploadBytes(clientPhotoRef, this.profileImageBlob)
        clientPhotoUrl = await getDownloadURL(clientPhotoRef)
      }

      const cardBlob = await this.renderFinalCardBlob()
      const documentDate = moment()
      const docId = this.docStorage.getDocumentName(documentDate, docType, 'idcard.png', this.trainerNameControl.value)
      const storageRef = this.docStorage.getDocumentStorageRef('dogs', this.dogId, docId)
      const docRef = this.docStorage.getDocumentFirebaseDoc('dogs', this.dogId, docId)

      await uploadBytes(storageRef, cardBlob)
      const downloadUrl = await getDownloadURL(storageRef)

      await setDoc(docRef, {
        documentDate: documentDate.toDate(),
        downloadUrl,
        fileType: 'image/png',
        type: docType,
        createdAt: new Date(),
        createdBy: this.auth.currentUser!.email,
        idCard: {
          trainer: this.trainerNameControl.value,
          idNumber: this.idCardNumberControl.value,
          expirationDate: this.idCardExpiryControl.value,
          clientPhotoUrl,
        },
      } as DocumentEntry)

      await this.docStorage.deleteExpiringDocumentEntry(this.dogId, docType!)

      this.snack.open('Igazolvány sikeresen mentve és feltöltve', 'Ok', { duration: 3000 })
    } catch (e) {
      console.error(e)
      this.snack.open('Nem sikerült menteni az igazolványt', 'Ajaj')
    } finally {
      this.isSaving = false
    }
  }
}
